import inventoryText from "./inventoryI18n.js";
import React, {
  useCallback,
  useEffect,
  useState,
} from "react";
import axios from "axios";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";

import { API_URL } from "../components/api";
import "./WarehouseTransferWorkflow.css";

const getApiMessage = (
  error,
  fallback
) => (
  error.response?.data?.detail
  || error.response?.data?.error
  || error.response?.data?.message
  || fallback
);

const productId = (product) => (
  product.product_id
  ?? product.id
);

const productName = (product) => (
  product.product_name
  || product.name
  || `Product ${productId(product)}`
);

const productQuantity = (product) => (
  Number(
    product.quantity
    ?? product.available_quantity
    ?? 0
  )
);

const requestItems = (request) => (
  Array.isArray(request.items)
    ? request.items
    : []
);

function WarehouseTransferWorkflow({
  warehouseId,
  warehouseName,
  onInventoryChanged,
}) {
  const [context, setContext] = useState(null);
  const [loadingContext, setLoadingContext] = (
    useState(true)
  );
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const [showTransfer, setShowTransfer] = (
    useState(false)
  );
  const [destinationId, setDestinationId] = (
    useState("")
  );
  const [reason, setReason] = useState("");
  const [selectedItems, setSelectedItems] = (
    useState({})
  );
  const [submitting, setSubmitting] = (
    useState(false)
  );

  const [decisionTarget, setDecisionTarget] = (
    useState(null)
  );
  const [decision, setDecision] = useState("");
  const [decisionReason, setDecisionReason] = (
    useState("")
  );
  const [deciding, setDeciding] = (
    useState(false)
  );

  const authHeaders = () => {
    const authTokens = JSON.parse(
      localStorage.getItem("authTokens")
      || "{}"
    );

    return {
      Authorization: (
        `Bearer ${authTokens.access}`
      ),
    };
  };

  const loadContext = useCallback(
    async () => {
      try {
        setLoadingContext(true);
        setError("");

        const response = await axios.get(
          `${API_URL}/inventory/transfer/`,
          {
            headers: authHeaders(),
            params: {
              warehouse_id: warehouseId,
            },
          }
        );

        setContext(response.data);
      } catch (requestError) {
        setError(
          getApiMessage(
            requestError,
            "Unable to load transfer controls."
          )
        );
        setContext(null);
      } finally {
        setLoadingContext(false);
      }
    },
    [warehouseId]
  );

  useEffect(() => {
    loadContext();
  }, [loadContext]);

  useEffect(() => {
    if (!notice) {
      return undefined;
    }

    const timeoutId = window.setTimeout(
      () => {
        setNotice("");
      },
      4000
    );

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [notice]);

  const products = Array.isArray(
    context?.products
  )
    ? context.products
    : [];

  const destinations = Array.isArray(
    context?.destinations
  )
    ? context.destinations
    : [];

  const incomingRequests = Array.isArray(
    context?.incoming_requests
  )
    ? context.incoming_requests
    : [];

  const currentWarehouseName = (
    context?.warehouse?.warehouse_name
    || context?.warehouse_name
    || warehouseName
    || `Warehouse ${warehouseId}`
  );

  const resetTransferForm = () => {
    setDestinationId("");
    setReason("");
    setSelectedItems({});
  };

  const openTransfer = () => {
    resetTransferForm();
    setError("");
    setNotice("");
    setShowTransfer(true);
  };

  const closeTransfer = () => {
    if (submitting) {
      return;
    }

    setShowTransfer(false);
    resetTransferForm();
  };

  const toggleProduct = (product) => {
    const id = String(productId(product));

    setSelectedItems((current) => {
      const next = { ...current };

      if (
        Object.prototype.hasOwnProperty.call(
          next,
          id
        )
      ) {
        delete next[id];
      } else {
        next[id] = 1;
      }

      return next;
    });
  };

  const changeQuantity = (
    product,
    value
  ) => {
    const id = String(productId(product));
    const available = productQuantity(product);
    let quantity = Number(value);

    if (!Number.isFinite(quantity)) {
      quantity = 1;
    }

    quantity = Math.max(
      1,
      Math.min(
        Math.floor(quantity),
        available
      )
    );

    setSelectedItems((current) => ({
      ...current,
      [id]: quantity,
    }));
  };

  const submitTransfer = async () => {
    const items = products
      .filter((product) => (
        Object.prototype.hasOwnProperty.call(
          selectedItems,
          String(productId(product))
        )
      ))
      .map((product) => ({
        product_id: Number(
          productId(product)
        ),
        quantity: Number(
          selectedItems[
            String(productId(product))
          ]
        ),
      }));

    if (!destinationId) {
      setError(
        "Select the recipient warehouse."
      );
      return;
    }

    if (items.length === 0) {
      setError(
        "Select at least one product."
      );
      return;
    }

    if (!reason.trim()) {
      setError(
        "A transfer reason is required."
      );
      return;
    }

    try {
      setSubmitting(true);
      setError("");
      setNotice("");

      const response = await axios.post(
        `${API_URL}/inventory/transfer/`,
        {
          from_warehouse_id: Number(
            warehouseId
          ),
          warehouse_id: Number(
            warehouseId
          ),
          to_warehouse_id: Number(
            destinationId
          ),
          reason: reason.trim(),
          products: items,
        },
        {
          headers: authHeaders(),
        }
      );

      setNotice(
        response.data?.message
        || (
          response.data?.auto_approved
            ? "Transfer completed."
            : "Transfer request submitted."
        )
      );

      setShowTransfer(false);
      resetTransferForm();
      await loadContext();

      if (
        response.data?.auto_approved
        && onInventoryChanged
      ) {
        onInventoryChanged();
      }
    } catch (requestError) {
      setError(
        getApiMessage(
          requestError,
          "Unable to submit the transfer."
        )
      );
    } finally {
      setSubmitting(false);
    }
  };

  const openDecision = (
    request,
    nextDecision
  ) => {
    setDecisionTarget(request);
    setDecision(nextDecision);
    setDecisionReason("");
    setError("");
    setNotice("");
  };

  const closeDecision = () => {
    if (deciding) {
      return;
    }

    setDecisionTarget(null);
    setDecision("");
    setDecisionReason("");
  };

  const submitDecision = async () => {
    if (!decisionTarget) {
      return;
    }

    if (
      decision === "reject"
      && !decisionReason.trim()
    ) {
      setError(
        "A rejection reason is required."
      );
      return;
    }

    const reference = (
      decisionTarget.request_reference
    );

    try {
      setDeciding(true);
      setError("");
      setNotice("");

      const response = await axios.post(
        (
          `${API_URL}/inventory/transfer/`
          + `${reference}/decision/`
        ),
        {
          decision,
          action: decision,
          reason: decisionReason.trim(),
          decision_reason: (
            decisionReason.trim()
          ),
        },
        {
          headers: authHeaders(),
        }
      );

      setNotice(
        response.data?.message
        || (
          decision === "approve"
            ? "Transfer approved."
            : "Transfer rejected."
        )
      );

      closeDecision();
      await loadContext();

      if (
        decision === "approve"
        && onInventoryChanged
      ) {
        onInventoryChanged();
      }
    } catch (requestError) {
      setError(
        getApiMessage(
          requestError,
          "Unable to process the request."
        )
      );
    } finally {
      setDeciding(false);
    }
  };

  const formatDate = (value) => {
    if (!value) {
      return "";
    }

    const parsed = new Date(value);

    if (Number.isNaN(parsed.getTime())) {
      return value;
    }

    return parsed.toLocaleString();
  };

  if (loadingContext) {
    return null;
  }

  if (error && !context) {
    return (
      <div className="warehouse-transfer-message warehouse-transfer-message--error">
        {error}
      </div>
    );
  }

  if (!context?.can_manage) {
    return null;
  }

  return (
    <section className="warehouse-transfer-workflow">
      <div className="warehouse-transfer-actions">
        <button
          type="button"
          className="AddClient warehouse-transfer-button"
          onClick={openTransfer}
        >
          {inventoryText("inventory.Transfer")}
        </button>

      </div>

      {notice && (
        <div className="warehouse-transfer-message warehouse-transfer-message--success">
          {notice}
        </div>
      )}

      {error && (
        <div className="warehouse-transfer-message warehouse-transfer-message--error">
          {error}
        </div>
      )}

      {incomingRequests.length > 0 && (
        <div className="warehouse-transfer-incoming">
          <h3>{inventoryText("inventory.IncomingTransferRequests")}</h3>

          {incomingRequests.map((request) => (
            <article
              className="warehouse-transfer-request"
              key={request.request_reference}
            >
              <div className="warehouse-transfer-request__header">
                <div>
                  <strong>
                    {request.from_warehouse_name
                      || request.source_warehouse_name
                      || "Source warehouse"}
                    {" → "}
                    {request.to_warehouse_name
                      || currentWarehouseName}
                  </strong>

                  <span>
                    Requested by{" "}
                    {request.initiator || "Unknown"}
                    {request.request_created_at && (
                      <>
                        {" · "}
                        {formatDate(
                          request.request_created_at
                        )}
                      </>
                    )}
                  </span>
                </div>

                <span className="warehouse-transfer-status">{inventoryText("inventory.Pending")}</span>
              </div>

              <div className="warehouse-transfer-request__reason">
                <strong>Reason:</strong>{" "}
                {request.reason || "-"}
              </div>

              <ul className="warehouse-transfer-products">
                {requestItems(request).map(
                  (item) => (
                    <li
                      key={
                        item.product_id
                        ?? item.id
                      }
                    >
                      {productName(item)}
                      {" — Quantity: "}
                      {item.quantity}
                    </li>
                  )
                )}
              </ul>

              <div className="warehouse-transfer-request__actions">
                <button
                  type="button"
                  className="edit-client"
                  onClick={() => (
                    openDecision(
                      request,
                      "approve"
                    )
                  )}
                >{inventoryText("inventory.Approve")}</button>

                <button
                  type="button"
                  className="warehouse-transfer-reject"
                  onClick={() => (
                    openDecision(
                      request,
                      "reject"
                    )
                  )}
                >{inventoryText("inventory.Reject")}</button>
              </div>
            </article>
          ))}
        </div>
      )}

      <Modal
        show={showTransfer}
        onHide={closeTransfer}
        className="warehouse-transfer-modal"
        size="lg"
        backdrop={submitting ? "static" : true}
      >
        <Modal.Header closeButton={!submitting}>
          <Modal.Title>{inventoryText("inventory.TransferProducts")}</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <div className="warehouse-transfer-field">
            <label>{inventoryText("inventory.FromWarehouse")}</label>
            <input
              type="text"
              value={currentWarehouseName}
              disabled
            />
          </div>

          <div className="warehouse-transfer-field">
            <label htmlFor="transfer-destination">{inventoryText("inventory.ToWarehouse")}</label>
            <select
              id="transfer-destination"
              value={destinationId}
              onChange={(event) => (
                setDestinationId(
                  event.target.value
                )
              )}
              disabled={submitting}
            >
              <option value="">
                Select warehouse
              </option>

              {destinations.map(
                (warehouse) => (
                  <option
                    key={
                      warehouse.warehouse_id
                    }
                    value={
                      warehouse.warehouse_id
                    }
                  >
                    {warehouse.warehouse_name}
                  </option>
                )
              )}
            </select>
          </div>

          <div className="warehouse-transfer-field">
            <label>{inventoryText("inventory.ProductsAndQuantities")}</label>

            <div className="warehouse-transfer-selection">
              {products.length === 0 && (
                <p>
                  {inventoryText("inventory.ThisWarehouseHasNoProducts")}
                  available for transfer.
                </p>
              )}

              {products.map((product) => {
                const id = String(
                  productId(product)
                );
                const available = (
                  productQuantity(product)
                );
                const selected = (
                  Object.prototype
                    .hasOwnProperty.call(
                      selectedItems,
                      id
                    )
                );

                return (
                  <div
                    className="warehouse-transfer-selection__row"
                    key={id}
                  >
                    <label>
                      <input
                        type="checkbox"
                        checked={selected}
                        disabled={
                          submitting
                          || available <= 0
                        }
                        onChange={() => (
                          toggleProduct(product)
                        )}
                      />

                      <span>
                        {productName(product)}
                        {" "}
                        <small>
                          Available: {available}
                        </small>
                      </span>
                    </label>

                    <input
                      type="number"
                      min="1"
                      max={available}
                      value={
                        selected
                          ? selectedItems[id]
                          : ""
                      }
                      disabled={
                        submitting
                        || !selected
                      }
                      onChange={(event) => (
                        changeQuantity(
                          product,
                          event.target.value
                        )
                      )}
                    />
                  </div>
                );
              })}
            </div>
          </div>

          <div className="warehouse-transfer-field">
            <label htmlFor="transfer-reason">{inventoryText("inventory.TransferReason")}</label>
            <textarea
              id="transfer-reason"
              value={reason}
              onChange={(event) => (
                setReason(event.target.value)
              )}
              placeholder="Enter the reason for this transfer"
              disabled={submitting}
              rows="3"
            />
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button
            className="warehouse-transfer-dialog-button"
            variant="secondary"
            onClick={closeTransfer}
            disabled={submitting}
          >{inventoryText("inventory.Cancel")}</Button>

          <Button
            className="warehouse-transfer-dialog-button"
            variant="primary"
            onClick={submitTransfer}
            disabled={submitting}
          >
            {submitting
              ? "Submitting..."
              : (
                context?.is_superadmin
                  ? "Transfer Now"
                  : "Submit Request"
              )}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={Boolean(decisionTarget)}
        onHide={closeDecision}
        className="warehouse-transfer-modal"
        backdrop={deciding ? "static" : true}
      >
        <Modal.Header closeButton={!deciding}>
          <Modal.Title>
            {decision === "approve"
              ? "Approve Transfer"
              : "Reject Transfer"}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <p>
            {decision === "approve"
              ? (
                "Approving will move the products "
                + "into this warehouse."
              )
              : (
                "Rejecting will leave all stock "
                + "quantities unchanged."
              )}
          </p>

          <div className="warehouse-transfer-field">
            <label htmlFor="decision-reason">
              {decision === "reject"
                ? "Rejection Reason"
                : "Decision Note"}
            </label>

            <textarea
              id="decision-reason"
              value={decisionReason}
              onChange={(event) => (
                setDecisionReason(
                  event.target.value
                )
              )}
              placeholder={
                decision === "reject"
                  ? "Reason is required"
                  : "Optional approval note"
              }
              disabled={deciding}
              rows="3"
            />
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button
            className="warehouse-transfer-dialog-button"
            variant="secondary"
            onClick={closeDecision}
            disabled={deciding}
          >{inventoryText("inventory.Cancel")}</Button>

          <Button
            className={
              "warehouse-transfer-dialog-button "
              + (
                decision === "reject"
                  ? "warehouse-transfer-dialog-button--danger"
                  : ""
              )
            }
            variant={
              decision === "approve"
                ? "primary"
                : "danger"
            }
            onClick={submitDecision}
            disabled={deciding}
          >
            {deciding
              ? "Saving..."
              : (
                decision === "approve"
                  ? "Approve"
                  : "Reject"
              )}
          </Button>
        </Modal.Footer>
      </Modal>
    </section>
  );
}

export default WarehouseTransferWorkflow;
